import { Link, useLocation } from 'react-router-dom';
import { User, Dumbbell, Calendar, Info, HelpCircle, LogOut, Users, UserCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../utils/cn';

const secondaryNavItems = [
  { path: '/about', label: 'About', icon: Info },
  { path: '/faq', label: 'FAQs', icon: HelpCircle },
];

export function Sidebar() {
  const location = useLocation();
  const { user, logout, isProvider, isAdmin } = useAuth();

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  // Main nav items - Calendar only for patients (not providers/admins)
  const mainNavItems = [
    { path: '/profile', label: 'Profile', icon: User },
    { path: '/program', label: 'Program', icon: Dumbbell },
    ...((isProvider || isAdmin) ? [] : [{ path: '/calendar', label: 'Calendar', icon: Calendar }]),
  ];

  return (
    <div className="hidden lg:flex lg:flex-shrink-0 lg:fixed lg:inset-y-0 lg:left-0 lg:w-64">
      <div className="flex flex-col w-64 bg-white border-r border-gray-200">
        {/* Header */}
        <div className="h-16 flex items-center px-4 border-b">
          <h1 className="text-xl font-bold text-primary">ACL Care</h1>
        </div>

        {/* User info */}
        <div className="px-4 py-4 border-b">
          <div className="flex items-center">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-primary font-semibold">
                {user?.first_name?.[0]?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-gray-500">
                {isAdmin ? 'Admin' : isProvider ? 'Provider' : 'Patient'}
              </p>
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          <div className="space-y-1">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname.startsWith(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "flex items-center px-3 py-2 text-sm font-medium rounded-md",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-gray-700 hover:bg-gray-50"
                  )}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
          </div>

          {/* Admin/Provider section */}
          {(isProvider || isAdmin) && (
            <div className="pt-4 mt-4 border-t border-gray-200">
              <p className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Management
              </p>
              
              {/* Patients - visible to both Admin and Providers */}
              <Link
                to="/clients"
                className={cn(
                  "flex items-center px-3 py-2 text-sm font-medium rounded-md",
                  location.pathname.startsWith('/clients')
                    ? "bg-primary/10 text-primary"
                    : "text-gray-700 hover:bg-gray-50"
                )}
              >
                <UserCircle className="mr-3 h-5 w-5" />
                Clients
              </Link>

              {/* Team/Users - visible to Admin only */}
              {isAdmin && (
                <Link
                  to="/users"
                  className={cn(
                    "flex items-center px-3 py-2 text-sm font-medium rounded-md",
                    location.pathname.startsWith('/users')
                      ? "bg-primary/10 text-primary"
                      : "text-gray-700 hover:bg-gray-50"
                  )}
                >
                  <Users className="mr-3 h-5 w-5" />
                  Admin
                </Link>
              )}
            </div>
          )}

          {/* Secondary Navigation */}
          <div className="pt-4 mt-4 border-t border-gray-200">
            <p className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Support
            </p>
            {secondaryNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "flex items-center px-3 py-2 text-sm font-medium rounded-md",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-gray-700 hover:bg-gray-50"
                  )}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Logout button */}
        <div className="p-4 border-t">
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md"
          >
            <LogOut className="mr-3 h-5 w-5" />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}